import type { DiffRow } from '../adapters/types';
import type { KnowledgeData } from '../adapters/knowledgeAdapter';
import type { AiProposal } from '@/types/ai-agent';
import { appendBscTextsToPage, type BscApplySection } from '@/components/common/AiChat/bscApplyOffer';
import { detectB4AspectFromText } from '@/components/common/AiChat/b4AspectDetect';

const SECTION_VITRI: Partial<Record<BscApplySection, number>> = {
  company_strengths: 2,
  industry_csf: 3,
  competitor_strengths: 4,
  competitors: 5,
  competitive_advantage: 6,
  strengths: 2,
  weaknesses: 11,
  opportunities: 12,
  threats: 13,
  strategy_so: 14,
  strategy_wo: 15,
  strategy_st: 16,
  strategy_wt: 17,
};

/** Section có sự kiện stream từng dòng trên trang (B1 EvaluatePage, B2 SWOT, B2 chiến lược). */
export const STREAM_SECTIONS: ReadonlySet<BscApplySection> = new Set(Object.keys(SECTION_VITRI) as BscApplySection[]);

const normalizeDedupe = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const isNearDuplicate = (a: string, b: string) => {
  if (!a || !b) return true;
  if (a === b) return true;
  const minLen = Math.min(a.length, b.length);
  if (minLen < 12) return a === b;
  return a.includes(b) || b.includes(a);
};

/** So sánh với list hiện có + loại trùng trong batch proposal. */
export function filterUniqueNewTexts(
  incoming: string[],
  existing: string[],
): { toAdd: string[]; skipped: number } {
  const seen = existing.map(normalizeDedupe).filter(Boolean);
  const toAdd: string[] = [];
  let skipped = 0;
  for (const raw of incoming) {
    const n = normalizeDedupe(raw);
    if (!n) {
      skipped += 1;
      continue;
    }
    if (seen.some((s) => isNearDuplicate(s, n))) {
      skipped += 1;
      continue;
    }
    seen.push(n);
    toAdd.push(raw.trim());
  }
  return { toAdd, skipped };
}

export function existingTextsForAppendSection(data: unknown, section: BscApplySection): string[] {
  const viTri = SECTION_VITRI[section];
  if (!viTri || !data || typeof data !== 'object') return [];
  const kd = data as KnowledgeData;
  const loaiId = kd.loaiByViTri?.[viTri];
  if (!loaiId) return [];
  return (kd.items ?? [])
    .filter((i) => i.loaiID === loaiId)
    .map((i) => String(i.noiDung ?? '').trim())
    .filter(Boolean);
}

const SECTION_BY_ADAPTER: Partial<Record<string, BscApplySection>> = {
  'b1.csf': 'industry_csf',
  'b1.strengths': 'company_strengths',
  'b1.competitors': 'competitors',
  'b1.competitor_strengths': 'competitor_strengths',
  'b1.advantage': 'competitive_advantage',
  'b2.swot': 'opportunities',
};

const STRATEGY_SECTION: Record<string, BscApplySection> = {
  SO: 'strategy_so',
  WO: 'strategy_wo',
  ST: 'strategy_st',
  WT: 'strategy_wt',
};

const plainText = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

export type ListEntry = { section: BscApplySection; text: string };

/**
 * Bước dạng danh sách (B1, SWOT, chiến lược) → các dòng gõ lần lượt vào trang.
 * Rỗng nghĩa là bước không phải dạng danh sách (B3/B4 dùng `appendProposalToPage`).
 */
export function listEntriesFromProposal(adapterKey: string, proposal: AiProposal, section?: BscApplySection): ListEntry[] {
  const items = proposal.items.filter((it) => it.op !== 'delete');
  if (adapterKey === 'b2.strategy') {
    return items.flatMap((it) => {
      const target = STRATEGY_SECTION[it.target.section];
      const text = String(it.fields.text ?? '').trim() || plainText(String(it.fields.html ?? ''));
      return target && text ? [{ section: target, text }] : [];
    });
  }
  if (!section || !STREAM_SECTIONS.has(section)) return [];
  return items.map((it) => String(it.fields.text ?? '').trim()).filter(Boolean).map((text) => ({ section, text }));
}

/**
 * Bước không phải danh sách (B3 chọn/kết quả, B4 bản đồ) → thêm một lượt lên trang qua sự kiện append
 * (chưa lưu; trang tự gắn nhãn AI). `rows` = adapter.diff(proposal, cur), cùng thứ tự proposal.items.
 * Trả về số mục đã gửi.
 */
export function appendProposalToPage(adapterKey: string, proposal: AiProposal, cur: unknown, rows: DiffRow[]): number {
  const items = proposal.items.filter((it) => it.op !== 'delete');
  const text = (it: AiProposal['items'][number]) => String(it.fields.text ?? '').trim();
  const nameOf = (id: string) =>
    String(((cur as KnowledgeData | null)?.items ?? []).find((i) => i.id === id)?.noiDung ?? '').trim();

  if (adapterKey === 'b3.select') {
    const names = items.filter((it) => it.fields.chon).map((it) => nameOf(it.target.id)).filter(Boolean);
    appendBscTextsToPage('strategy_chon', names);
    return names.length;
  }
  if (adapterKey === 'b3.result') {
    const results = items
      .map((it) => ({ name: nameOf(it.target.id), text: String(it.fields.result_html ?? '').trim() }))
      .filter((r) => r.name && r.text);
    appendBscTextsToPage('strategy_ketqua', results.map((r) => r.text), { items: results });
    return results.length;
  }
  if (adapterKey === 'b4.theme_map' || adapterKey === 'b4.company_map') {
    const texts = items.map(text).filter(Boolean);
    const aspects = items
      .filter((it) => text(it))
      .map((it) => detectB4AspectFromText(rows.find((r) => r.ref === it.target.ref)?.sectionLabel ?? ''));
    appendBscTextsToPage(adapterKey === 'b4.theme_map' ? 'b4_objective_theme' : 'b4_objective_company', texts, { aspects });
    return texts.length;
  }
  throw new Error('Chưa hỗ trợ thêm vào trang cho bước này.');
}

export function appendSelectedRows(
  adapterKey: string,
  rows: DiffRow[],
  selected: Set<string>,
  edits: Record<string, string>,
  swotSection?: BscApplySection,
) {
  const section = swotSection ?? SECTION_BY_ADAPTER[adapterKey];
  if (!section) throw new Error('Chưa hỗ trợ Thêm vào trang cho bước này.');
  const texts = rows
    .filter((r) => selected.has(r.ref) && r.op !== 'delete')
    .map((r) => (edits[r.ref] ?? r.after).trim())
    .filter(Boolean);
  appendBscTextsToPage(section, texts);
}

/**
 * Drawer «✨ AI gợi ý» ở chế độ «đưa ra màn chính»: thêm các mục đã chọn vào trang cho MỌI bước (không ghi DB).
 * B1 / SWOT / chiến lược: theo nhóm; B3 / B4: `appendProposalToPage`; B5: sự kiện `bsc-ai-append-b5` (trang hỏi đầu cá).
 */
export async function appendChosenToPage(
  adapter: { key: string; load: () => Promise<unknown> },
  proposal: AiProposal,
  rows: DiffRow[],
  selected: Set<string>,
  edits: Record<string, string>,
  swotSection?: BscApplySection,
): Promise<number> {
  const chosen = rows.filter((r) => selected.has(r.ref) && r.op !== 'delete');
  const textOf = (r: DiffRow) => (edits[r.ref] ?? r.after).trim();
  if (swotSection || SECTION_BY_ADAPTER[adapter.key]) {
    appendSelectedRows(adapter.key, rows, selected, edits, swotSection);
    return chosen.length;
  }
  const items = proposal.items.filter((it) => selected.has(it.target.ref) && it.op !== 'delete');
  if (adapter.key === 'b2.strategy') {
    const bySection = new Map<BscApplySection, string[]>();
    items.forEach((it) => {
      const target = STRATEGY_SECTION[it.target.section];
      const row = chosen.find((r) => r.ref === it.target.ref);
      const text = row ? textOf(row) : String(it.fields.text ?? '').trim();
      if (target && text) bySection.set(target, [...(bySection.get(target) ?? []), text]);
    });
    bySection.forEach((texts, section) => appendBscTextsToPage(section, texts));
    return items.length;
  }
  if (adapter.key.startsWith('b5.')) {
    const kind = adapter.key === 'b5.csf' ? 'bone' : 'kpi';
    const texts = chosen.map(textOf).map((t) => t.split(' — ')[0].trim()).filter(Boolean);
    window.dispatchEvent(new CustomEvent('bsc-ai-append-b5', { detail: { kind, texts } }));
    return texts.length;
  }
  const subset = { ...proposal, items };
  return appendProposalToPage(adapter.key, subset, await adapter.load(), rows);
}
